import { PlusSignIcon } from "@hugeicons/core-free-icons"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  ScrollArea,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@motleyhand/design"
import { Link, useParams } from "@tanstack/react-router"
import { useState } from "react"
import { CONFIG } from "./definitionMenuConfig"
import type { KnowledgeBase } from "@/rag"
import { scrollIntoViewRef } from "@/utils"
import type { Evaluation } from "@/evaluation"
import type { Prompt } from "@/prompting"
import { useCurrentTenant } from "@/tenant"
import type { Workflow } from "@/workflow"

type Props = {
  type: keyof typeof CONFIG
  definitions: Prompt[] | Workflow[] | Evaluation[] | KnowledgeBase[]
  onCreateClick: () => void | Promise<void>
  searchQuery: string
}

export default function DefinitionMenu({
  type,
  definitions,
  onCreateClick,
  searchQuery,
}: Props) {
  const { tenantSlug } = useCurrentTenant()
  const currentId = useParams({ strict: false }).id
  const hasActiveItem = definitions.some(({ id }) => id === currentId)
  const [menuOpen, setMenuOpen] = useState(hasActiveItem)
  const [prev, setPrev] = useState({ id: currentId, active: hasActiveItem })
  const { open: sidebarOpen, setOpen: setSidebarOpen } = useSidebar()

  const filtered = definitions.filter(({ name }) =>
    name.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  if (currentId !== prev.id || hasActiveItem !== prev.active) {
    setPrev({ id: currentId, active: hasActiveItem })
    if (hasActiveItem && (currentId !== prev.id || !prev.active)) {
      setMenuOpen(true)
    }
  }

  const handleMenuOpenChange = (doOpen: boolean) => {
    setMenuOpen(!sidebarOpen || doOpen)
    setSidebarOpen(true)
  }

  return (
    <SidebarMenu>
      <Collapsible
        open={sidebarOpen && (menuOpen || !!searchQuery)}
        onOpenChange={handleMenuOpenChange}
        className="group/collapsible"
      >
        <SidebarMenuItem>
          <CollapsibleTrigger
            render={
              <SidebarMenuButton>
                <HugeiconsIcon icon={CONFIG[type].icon} />
                {CONFIG[type].label}
                {filtered.length > 0 && (
                  <div className="translate-y-px text-xs text-muted-foreground">
                    {filtered.length}
                  </div>
                )}
              </SidebarMenuButton>
            }
          />
          <SidebarMenuAction
            onClick={onCreateClick}
            title={`Create ${type}`}
            className="opacity-0 transition-opacity group-hover/collapsible:opacity-100"
          >
            <HugeiconsIcon icon={PlusSignIcon} />
          </SidebarMenuAction>
          <CollapsibleContent>
            <ScrollArea className="**:data-[slot=scroll-area-viewport]:max-h-40">
              <SidebarMenuSub className="py-0">
                {definitions.length === 0 && !searchQuery ? (
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      onClick={onCreateClick}
                      className="h-auto py-1 text-muted-foreground hover:text-foreground"
                    >
                      Create your first {CONFIG[type].name}
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ) : (
                  filtered.map(({ id, name }) => (
                    <SidebarMenuSubItem
                      key={id}
                      ref={id === currentId ? scrollIntoViewRef() : undefined}
                    >
                      <SidebarMenuSubButton
                        isActive={id === currentId}
                        render={
                          <Link
                            to={`/t/$tenantSlug/${type}s/$id`}
                            params={{
                              tenantSlug,
                              id,
                            }}
                            className="h-auto py-1"
                            title={name}
                          >
                            {name}
                          </Link>
                        }
                      />
                    </SidebarMenuSubItem>
                  ))
                )}
              </SidebarMenuSub>
            </ScrollArea>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    </SidebarMenu>
  )
}
